import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import { postURLs } from "../constants/url";
import { GetSponsoredPost, GetSingleSponsoredPost } from "../API/PostApi";
import Navbar from "../layouts/Navbar";
import Footer from "../layouts/Footer";
import PostItem from "./PostItem";
import { viewPropertyUAE, viewPropertyUK } from "../utils";
import { HOME_PAGE_SIZE, loadHomePage, readPage, pageNumbers } from "./homeFeedPaging";

// Keep a bounded set of pages in memory when a reader opens a post and returns.
// No tokens or post contents are written to browser storage.
const feedCache = new Map();
let cacheOwner;
const positionCache = new Map();
function readPosition(key) {
  if (positionCache.has(key)) return positionCache.get(key);
  try {
    const value = JSON.parse(sessionStorage.getItem(key));
    return value && Number.isFinite(value.top) ? value : null;
  } catch (_) { return null; }
}
function writePosition(key, value) {
  positionCache.set(key, value);
  if (positionCache.size > 100) positionCache.delete(positionCache.keys().next().value);
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
}

export default function Index() {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentPage = readPage(params.get("page"));
  const categorySlug = params.get("category") || "";
  const Token = localStorage.getItem("token");
  let user = null;
  try { user = JSON.parse(localStorage.getItem("user")); } catch (_) {}
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [sponsoredPost, setSponsoredPost] = useState([]);
  const [imagePriview, setImagePriview] = useState("");
  const [retry, setRetry] = useState(0);
  const containerRef = useRef(null);
  const feedRef = useRef(null);
  const restoringRef = useRef(false);
  const readyRef = useRef(false);
  const cacheKey = location.pathname + location.search;
  const positionKey = "askproperty:feed:v1:" + (user?.id || "guest") + ":" +
    (location.key || "initial") + ":" + cacheKey;
  if (cacheOwner !== Token) {
    feedCache.clear();
    positionCache.clear();
    cacheOwner = Token;
  }
  const [loaded, setLoaded] = useState(() => ({
    key: cacheKey, owner: Token, result: feedCache.get(cacheKey) || null, error: ""
  }));
  const result = loaded.key === cacheKey && loaded.owner === Token ? loaded.result : null;
  const error = loaded.key === cacheKey && loaded.owner === Token ? loaded.error : "";
  const postsLoading = !result && !error;
  readyRef.current = Boolean(result);

  const pageHref = page => {
    const query = new URLSearchParams(location.search);
    if (page === 1) query.delete("page");
    else query.set("page", String(page));
    const text = query.toString();
    return location.pathname + (text ? "?" + text : "");
  };

  useEffect(() => {
    function resize() { setWindowWidth(window.innerWidth); }
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    let mounted = true;
    GetSponsoredPost().then(res => {
      if (mounted && res.data.status === "success") setSponsoredPost(res.data.data);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    const cached = feedCache.get(cacheKey);
    if (cached && !retry) {
      setLoaded({ key: cacheKey, owner: Token, result: cached, error: "" });
      return () => { mounted = false; };
    }
    setLoaded({ key: cacheKey, owner: Token, result: null, error: "" });
    const request = async page => {
      const response = await axios({
        method: "POST", url: postURLs.Url,
        params: { page },
        data: categorySlug ? { category_slug: categorySlug } : {},
        headers: Token ? { Authorization: "Bearer " + Token } : {},
        timeout: 20000
      });
      return response.data.data;
    };
    loadHomePage(request, currentPage).then(value => {
      if (!mounted) return;
      if (value.page !== currentPage) {
        const query = new URLSearchParams(location.search);
        if (value.page === 1) query.delete("page");
        else query.set("page", String(value.page));
        history.replace({ pathname: location.pathname, search: query.toString() });
        return;
      }
      feedCache.set(cacheKey, value);
      if (feedCache.size > 12) feedCache.delete(feedCache.keys().next().value);
      setLoaded({ key: cacheKey, owner: Token, result: value, error: "" });
    }).catch(() => {
      if (mounted) setLoaded({ key: cacheKey, owner: Token, result: null,
        error: "We could not load these posts. Please try again." });
    });
    return () => { mounted = false; };
  }, [cacheKey, currentPage, categorySlug, Token, history, location.pathname, location.search, retry]);

  useEffect(() => {
    if (location.pathname !== "/") return;
    let link = document.head.querySelector('link[rel="canonical"]');
    const existed = Boolean(link);
    const previous = link ? link.getAttribute("href") : null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    const query = new URLSearchParams();
    if (categorySlug) query.set("category", categorySlug);
    if (currentPage > 1) query.set("page", String(currentPage));
    link.setAttribute("href", "https://askproperty.net/" + (query.toString() ? "?" + query.toString() : ""));
    return () => {
      if (!existed) link.remove();
      else if (previous === null) link.removeAttribute("href");
      else link.setAttribute("href", previous);
    };
  }, [location.pathname, categorySlug, currentPage]);

  const savePosition = event => {
    const container = containerRef.current;
    if (!container || !readyRef.current || restoringRef.current) return;
    const bounds = container.getBoundingClientRect();
    const clicked = event?.type === "click" && event.target.closest
      ? event.target.closest("[data-home-post]") : null;
    const anchor = clicked || Array.from(container.querySelectorAll("[data-home-post]"))
      .find(node => node.getBoundingClientRect().bottom > bounds.top);
    writePosition(positionKey, {
      top: container.scrollTop, windowTop: window.scrollY,
      slug: anchor ? anchor.getAttribute("data-home-post") : null,
      offset: anchor ? anchor.getBoundingClientRect().top - bounds.top : 0
    });
  };

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !result) return;
    const saved = readPosition(positionKey);
    if (!saved) { container.scrollTop = 0; return; }
    restoringRef.current = true;
    let active = true;
    const restore = () => {
      if (!active) return;
      const anchor = Array.from(container.querySelectorAll("[data-home-post]"))
        .find(node => node.getAttribute("data-home-post") === saved.slug);
      container.scrollTop = anchor
        ? container.scrollTop + anchor.getBoundingClientRect().top -
          container.getBoundingClientRect().top - saved.offset
        : saved.top;
      if (Number.isFinite(saved.windowTop)) window.scrollTo(0, saved.windowTop);
    };
    const stop = () => { active = false; restoringRef.current = false; };
    const keyStop = event => {
      if (["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Home", "End", " ", "Tab"].includes(event.key)) stop();
    };
    restore();
    const frame = requestAnimationFrame(restore);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(restore) : null;
    if (observer && feedRef.current) observer.observe(feedRef.current);
    const timer = setTimeout(stop, 8000);
    container.addEventListener("wheel", stop, { passive: true });
    container.addEventListener("touchstart", stop, { passive: true });
    container.addEventListener("pointerdown", stop, { passive: true });
    container.addEventListener("keydown", keyStop);
    container.addEventListener("click", stop, true);
    return () => {
      stop(); clearTimeout(timer); cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      container.removeEventListener("wheel", stop);
      container.removeEventListener("touchstart", stop);
      container.removeEventListener("pointerdown", stop);
      container.removeEventListener("keydown", keyStop);
      container.removeEventListener("click", stop, true);
    };
  }, [positionKey, result]);

  const handleSponsoredPostClick = slug => {
    GetSingleSponsoredPost(slug).then(res => {
      window.open(res.data.data.sponsored_url, "_blank");
    });
  };

  const pagination = result && result.lastPage > 1 ? (
    <nav aria-label="Post pages" style={{ display: "flex", flexWrap: "wrap", gap: "6px", justifyContent: "center", padding: "16px 0" }}>
      {currentPage > 1 && <Link className="btn btn-light" to={pageHref(currentPage - 1)}>Previous</Link>}
      {pageNumbers(currentPage, result.lastPage).map(number => typeof number === "string" ?
        <span key={number} aria-hidden="true" style={{ padding: "8px 2px" }}>…</span> :
        <Link key={number} to={pageHref(number)} aria-label={"Page " + number}
          aria-current={number === currentPage ? "page" : undefined}
          onClick={event => { if (number === currentPage) event.preventDefault(); }}
          className="btn btn-light" style={number === currentPage ? { background: "#63af30", color: "white" } : {}}>
          {number}
        </Link>)}
      {currentPage < result.lastPage && <Link className="btn btn-light" to={pageHref(currentPage + 1)}>Next</Link>}
    </nav>
  ) : null;

  return (
    <>
      <Navbar />
        <div className="main">
          <div
            className="container hide-scrollbar"
            ref={containerRef}
            style={{
              height: "90vh",
              overflow: "auto",
            }}
            onScroll={savePosition}
            onClickCapture={savePosition}
          >
            <div className={`row ${windowWidth < 450 ? "pt-3" : "pt-5"}`}>
              <div className="col-lg-3 left-sidebar pb-5 d-lg-block">
                <div
                  className={`widget ${windowWidth < 450 ? "pb-2" : "pb-5"}`}
                >
                  
                  <ul
                    className={`mx-2 ${
                      windowWidth < 450 ? "d-flex mt-0 mb-0" : "mt-2"
                    }`}
                  >
                    <li
                    // onClick={()=>{
                    //     history.push('/listing', '_blank')
                    // }}
                    >
                      <Link
                        className={`${
                          windowWidth > 450 ? "d-flex" : ""
                        } align-items-center show-pointer`}
                        to="/listing"
                        target="_blank"
                      >
                        <img
                          className="pe-3"
                          style={{
                            width: "90px",
                            height: "50px",
                          }}
                          src={"assets/image/south-africa.png"}
                        />
                        <p
                          style={{
                            fontSize: windowWidth < 450 ? "12px" : "",
                            lineHeight: windowWidth < 450 ? "18px" : "",
                          }}
                        >
                          {windowWidth < 450
                            ? "Properties in SA"
                            : "View Properties in South Africa"}
                        </p>
                      </Link>
                    </li>
                    <li className="">
                      <a
                        className={`${
                          windowWidth > 450 ? "d-flex mt-3" : ""
                        } align-items-center show-pointer`}
                        href={viewPropertyUK}
                        target="_blank"
                      >
                        <img
                          style={{
                            width: "90px",
                            height: "50px",
                          }}
                          className="pe-3"
                          src={"assets/image/ukflag.png"}
                        />
                        <p
                          style={{
                            fontSize: windowWidth < 450 ? "12px" : "",
                            lineHeight: windowWidth < 450 ? "18px" : "",
                          }}
                        >
                          {windowWidth < 450
                            ? "Properties in UK"
                            : "View Properties in UK"}
                        </p>
                      </a>
                    </li>
                    <li className="">
                      <a
                        className={`${
                          windowWidth > 450 ? "d-flex mt-3" : ""
                        } align-items-center show-pointer`}
                        href={viewPropertyUAE}
                        target="_blank"
                      >
                        <img
                          style={{
                            width: "90px",
                            height: "50px",
                          }}
                          className="pe-3"
                          src={"assets/image/uae.png"}
                          // src={"assets/image/nigeria.png"}
                        />
                        <p
                          style={{
                            fontSize: windowWidth < 450 ? "12px" : "",
                            lineHeight: windowWidth < 450 ? "18px" : "",
                          }}
                        >
                          {/* {windowWidth < 450
                            ? "Properties in Nigeria"
                            : "View Properties in Nigeria"} */}
                          {windowWidth < 450
                            ? "Properties in UAE"
                            : "View Properties in UAE"}
                        </p>
                      </a>
                    </li>

                    {windowWidth < 450 && (
                      <li>
                        <a
                          className={`${
                            windowWidth > 450 ? "d-flex mt-3" : ""
                          } align-items-center show-pointer`}
                          href="/apply-for-home-loan"
                          target="_blank"
                        >
                          <img
                            style={{
                              width: "90px",
                              height: "50px",
                            }}
                            className="pe-3"
                            src={"assets/image/home.jpg"}
                          />
                          <p
                            style={{
                              fontSize: windowWidth < 450 ? "12px" : "",
                              lineHeight: windowWidth < 450 ? "18px" : "",
                            }}
                          >
                            {"Apply for a home loan"}
                          </p>
                        </a>
                      </li>
                    )}
                  </ul>

                  {windowWidth > 450 && (
                    <div className="mx-2 mt-5 d-flex align-items-center justify-content-center">
                      <a
                        href="/apply-for-home-loan"
                        target="_blank"
                        className="w-100"
                      >
                        <button
                          style={{
                            backgroundColor: "#63af30",
                            borderRadius: "10px",
                            color: "white",
                            padding: "10px",
                          }}
                          className="border-0 px-3"
                        >
                          Apply for a home loan
                        </button>
                      </a>
                    </div>
                  )}
                  {windowWidth > 450 && (
                    <div
                      className="mx-2"
                      style={{
                        marginTop: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <a
                        href="/apply-for-pre-approval"
                        target="_blank"
                        className="w-100"
                      >
                        <button
                          style={{
                            backgroundColor: "#63af30",
                            borderRadius: "10px",
                            color: "white",
                            padding: "10px",
                          }}
                          className="border-0 px-3"
                        >
                          Apply for pre-approval
                        </button>
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-lg-6 middle-area mb-5">
                <div className="search-box mb-4 bg-white p-sm-3 p-2">
                  <div className="d-flex align-items-center w-100">
                    {Token === null ? (
                      " "
                    ) : user?.image === null ? (
                      <span className="user-icon">
                        <img src="assets/image/dummy_image.png" alt="" />
                      </span>
                    ) : (
                      <span className="user-icon">
                        {" "}
                        <img src={user?.image} alt="" />
                      </span>
                    )}

                    <form action="#" className="search-post w-100 ">
                      <Link to="/add-post">
                        <input
                          type="text"
                          placeholder="What is your property question or advice?"
                        />
                      </Link>
                    </form>
                  </div>
                </div>

                {pagination}
                <div ref={feedRef} aria-busy={postsLoading}>
                  {postsLoading && <p role="status" className="text-center p-4">Loading posts…</p>}
                  {error && <div role="alert" className="text-center p-4">
                    <p>{error}</p>
                    <button type="button" className="btn btn-light" onClick={() => setRetry(value => value + 1)}>Try again</button>
                  </div>}
                  {result && <p className="text-center text-muted" role="status">
                    {result.total ? "Posts " + ((currentPage - 1) * HOME_PAGE_SIZE + 1) + "–" +
                      Math.min(currentPage * HOME_PAGE_SIZE, result.total) + " of " + result.total : "No posts found."}
                  </p>}
                  {result && result.items.map((item, i) => (
                    <div key={item.slug || item.id} data-home-post={String(item.slug || item.id)}>
                      <PostItem item={item} i={i} setImagePriview={setImagePriview} />
                    </div>
                  ))}
                </div>
                {pagination}
              </div>
              <div className="col-lg-3 right-sidebar d-lg-block d-none">
                <div className="widget">
                  <h3 className="widget-title">Sponsored Post</h3>
                  <div className="liked-post">
                    {sponsoredPost.map((item, i) => {
                      return (
                        <div
                          key={i}
                          className="card mb-2"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSponsoredPostClick(item?.slug)}
                        >
                          {item?.image ? (
                            <img
                              src={item?.image}
                              alt={item?.company}
                              className="card-img-top"
                            />
                          ) : (
                            ""
                          )}

                          <div className="card-body py-0">
                            <h5 className="card-title">{item?.company}</h5>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      <div className="modal fade" id="myModalpop">
        <div className="modal-dialog modal-dialog-centered">
          <div
            className="modal-content image-pop"
            style={{ background: "transparent", border: "none" }}
          >
            <button
              type="button"
              className="close text-right close-btn popup_btn"
              data-bs-dismiss="modal"
              aria-label="Close"
              style={{ color: "red", fontWeight: 900 }}
            >
              &times;
            </button>

            {imagePriview !== null ? (
              <img
                src={imagePriview}
                className="popup_image"
                style={{ height: "90%", width: "90%" }}
              />
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
